import React, { useEffect, useState, useRef, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { useParams, useNavigate, Link } from 'react-router-dom';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import ShareExamModal from '../../components/ShareExamModal';

// Math symbol palette — split into 4 sections
const MATH_SYMBOL_SECTIONS = [
  {
    key: 'algebraic', label: '🔢 Algebraic',
    symbols: [
      { label: 'a/b', insert: '\\frac{a}{b}' }, { label: '√x', insert: '\\sqrt{x}' },
      { label: 'ⁿ√x', insert: '\\sqrt[n]{x}' }, { label: 'x²', insert: 'x^{2}' },
      { label: 'xₙ', insert: 'x_{n}' }, { label: '±', insert: '\\pm' },
      { label: '×', insert: '\\times' }, { label: '÷', insert: '\\div' },
      { label: '≤', insert: '\\leq' }, { label: '≥', insert: '\\geq' },
      { label: '≠', insert: '\\neq' }, { label: '≈', insert: '\\approx' },
      { label: '∞', insert: '\\infty' }, { label: '|x|', insert: '|x|' },
      { label: '⌊x⌋', insert: '\\lfloor x \\rfloor' }, { label: '⌈x⌉', insert: '\\lceil x \\rceil' },
    ],
  },
  {
    key: 'trig', label: '📐 Trigonometry',
    symbols: [
      { label: 'sin', insert: '\\sin' }, { label: 'cos', insert: '\\cos' },
      { label: 'tan', insert: '\\tan' }, { label: 'cot', insert: '\\cot' },
      { label: 'cosec', insert: '\\csc' }, { label: 'sec', insert: '\\sec' },
      { label: 'sin⁻¹', insert: '\\sin^{-1}' }, { label: 'cos⁻¹', insert: '\\cos^{-1}' },
      { label: 'tan⁻¹', insert: '\\tan^{-1}' }, { label: '°', insert: '^{\\circ}' },
      { label: 'rad', insert: '\\text{ rad}' }, { label: 'π', insert: '\\pi' },
      { label: '2π', insert: '2\\pi' }, { label: 'π/2', insert: '\\frac{\\pi}{2}' },
    ],
  },
  {
    key: 'greek', label: '🏛️ Greek',
    symbols: [
      { label: 'α', insert: '\\alpha' }, { label: 'β', insert: '\\beta' },
      { label: 'γ', insert: '\\gamma' }, { label: 'δ', insert: '\\delta' },
      { label: 'θ', insert: '\\theta' }, { label: 'λ', insert: '\\lambda' },
      { label: 'μ', insert: '\\mu' }, { label: 'σ', insert: '\\sigma' },
      { label: 'ω', insert: '\\omega' }, { label: 'φ', insert: '\\phi' },
      { label: 'ψ', insert: '\\psi' }, { label: 'ε', insert: '\\epsilon' },
      { label: 'η', insert: '\\eta' }, { label: 'ρ', insert: '\\rho' },
      { label: 'Δ', insert: '\\Delta' }, { label: 'Σ', insert: '\\Sigma' },
      { label: 'Ω', insert: '\\Omega' }, { label: 'Π', insert: '\\Pi' },
    ],
  },
  {
    key: 'calculus', label: '∫ Calculus',
    symbols: [
      { label: '∫f dx', insert: '\\int f(x)\\,dx' }, { label: '∫ₐᵇ', insert: '\\int_{a}^{b} f(x)\\,dx' },
      { label: 'Σᵢ₌₁ⁿ', insert: '\\sum_{i=1}^{n}' }, { label: 'lim', insert: '\\lim_{x \\to \\infty}' },
      { label: 'lim x→a', insert: '\\lim_{x \\to a}' }, { label: 'd/dx', insert: '\\frac{d}{dx}' },
      { label: 'd²/dx²', insert: '\\frac{d^2}{dx^2}' }, { label: '∂/∂x', insert: '\\frac{\\partial f}{\\partial x}' },
      { label: '∇', insert: '\\nabla' }, { label: 'log', insert: '\\log' },
      { label: 'ln', insert: '\\ln' }, { label: 'logₐ', insert: '\\log_{a}' },
      { label: 'eˣ', insert: 'e^{x}' },
    ],
  },
];

const ARTS_SYMBOLS = [
  { label: 'α', insert: '\\alpha' }, { label: 'β', insert: '\\beta' },
  { label: 'γ', insert: '\\gamma' }, { label: 'δ', insert: '\\delta' },
  { label: 'θ', insert: '\\theta' }, { label: 'λ', insert: '\\lambda' },
  { label: 'μ', insert: '\\mu' }, { label: 'σ', insert: '\\sigma' },
  { label: 'ω', insert: '\\omega' }, { label: 'Δ', insert: '\\Delta' },
  { label: 'Σ', insert: '\\Sigma' }, { label: 'Ω', insert: '\\Omega' },
  { label: '→', insert: '\\rightarrow' }, { label: '←', insert: '\\leftarrow' },
  { label: '↔', insert: '\\leftrightarrow' },
];

// Render $latex$ for student view / question list
function MathPreview({ text }) {
  const ref = useRef();
  useEffect(() => {
    if (!ref.current || !text) return;
    if (window.katex) {
      try {
        const parts = text.split(/(\$[^$]+\$)/g);
        ref.current.innerHTML = parts.map(p => {
          if (p.startsWith('$') && p.endsWith('$')) {
            try { return window.katex.renderToString(p.slice(1,-1), { throwOnError: false }); }
            catch { return p; }
          }
          return p.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
        }).join('');
      } catch { ref.current.textContent = text; }
    } else { ref.current.textContent = text; }
  }, [text]);
  return <span ref={ref} />;
}

// Convert stored $latex$ text → Quill HTML with rendered chips
function storageToHtml(text) {
  if (!text) return '';
  const parts = text.split(/(\$[^$]+\$)/g);
  return parts.map(p => {
    if (p.startsWith('$') && p.endsWith('$')) {
      const latex = p.slice(1, -1);
      let rendered = latex;
      if (window.katex) {
        try { rendered = window.katex.renderToString(latex, { throwOnError: false }); }
        catch { rendered = latex; }
      }
      return `<span class="ql-math-chip" data-latex="${latex.replace(/"/g, '&quot;')}">${rendered}</span>`;
    }
    return p.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }).join('');
}

// Convert Quill HTML → stored $latex$ text
function htmlToStorage(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  // Replace math chip spans with $latex$
  div.querySelectorAll('.ql-math-chip').forEach(span => {
    const latex = span.getAttribute('data-latex') || '';
    const text = document.createTextNode('$' + latex + '$');
    span.parentNode.replaceChild(text, span);
  });
  // Get text, strip remaining HTML tags
  let text = div.innerHTML
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<p>/gi, '')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&nbsp;/g, ' ')
    .replace(/\n+$/g, '')
    .trim();
  return text;
}

// Register Quill math-chip blot once
let quillBlotRegistered = false;
function registerQuillBlot() {
  if (quillBlotRegistered || !window.Quill) return;
  try {
    const Embed = window.Quill.import('blots/embed');
    class MathChipBlot extends Embed {
      static create(value) {
        const node = super.create();
        node.setAttribute('data-latex', value.latex || '');
        node.setAttribute('contenteditable', 'false');
        if (window.katex) {
          try { node.innerHTML = window.katex.renderToString(value.latex || '', { throwOnError: false }); }
          catch { node.textContent = value.latex; }
        } else { node.textContent = value.latex; }
        return node;
      }
      static value(node) { return { latex: node.getAttribute('data-latex') || '' }; }
    }
    MathChipBlot.blotName = 'math-chip';
    MathChipBlot.tagName = 'span';
    MathChipBlot.className = 'ql-math-chip';
    window.Quill.register(MathChipBlot, true);
    quillBlotRegistered = true;
  } catch(e) {}
}

// MathLive popup for visual equation building
function MathEquationPopup({ onInsert, onClose }) {
  const mlRef = useRef();
  useEffect(() => { setTimeout(() => { mlRef.current?.focus(); }, 100); }, []);

  const handleInsert = () => {
    const mf = mlRef.current;
    if (!mf) return;
    const latex = mf.getValue('latex');
    if (latex && latex.trim()) { onInsert(latex.trim()); }
    else { toast.error('Please enter an equation first'); }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      background: 'rgba(0,0,0,0.75)', zIndex: 99999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'var(--card)', borderRadius: 16, padding: 24,
        maxWidth: 560, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
        border: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ fontWeight: 700, fontSize: 16, margin: 0 }}>∑ Insert Equation</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text2)', lineHeight: 1 }}>✕</button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>
          Build your equation visually — no LaTeX needed! Use the keyboard icon for more symbols.
        </p>
        <div style={{ background: 'white', borderRadius: 10, padding: '14px 16px', marginBottom: 16, border: '2px solid var(--primary)', minHeight: 70, display: 'flex', alignItems: 'center' }}>
          <math-field
            ref={mlRef}
            style={{ width: '100%', fontSize: 26, color: '#111', '--hue': '250' }}
            virtual-keyboard-mode="manual"
          ></math-field>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleInsert}>✅ Insert Equation</button>
        </div>
      </div>
    </div>
  );
}

// Quill-based rich text editor with math chip support
function QuillEditor({ id, value, onChange, placeholder, minHeight = 80, showMath = false }) {
  const containerRef = useRef();
  const quillRef = useRef(null);
  const [showPopup, setShowPopup] = useState(false);
  const isInitialized = useRef(false);
  const lastValue = useRef(value);
  // Quill's own event listeners are attached once, in the mount-only effect below, so any
  // closure they capture is frozen at that first render forever. Routing every call through
  // this ref (kept current on every render) means the listener always invokes the CALLER'S
  // latest onChange, never a stale one — regardless of how many times the parent re-renders.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Initialize Quill
  useEffect(() => {
    if (!containerRef.current || !window.Quill || isInitialized.current) return;
    registerQuillBlot();
    isInitialized.current = true;

    const quill = new window.Quill(containerRef.current, {
      theme: 'snow',
      placeholder: placeholder || '',
      modules: {
        toolbar: [['bold', 'italic', 'underline'], ['clean']],
      },
    });

    quillRef.current = quill;
    // Register as active quill for arts teacher symbol insertion
    window._activeQuill = quill;

    // Set initial value
    if (value) {
      quill.clipboard.dangerouslyPasteHTML(storageToHtml(value));
    }

    // On change — convert back to storage format
    quill.on('text-change', () => {
      window._activeQuill = quill;
      const html = quill.root.innerHTML;
      const stored = htmlToStorage(html);
      lastValue.current = stored;
      onChangeRef.current(stored);
    });

    quill.on('selection-change', () => { window._activeQuill = quill; });

    return () => { isInitialized.current = false; };
  }, []);

  // Sync external value changes into Quill (e.g. on edit question load)
  useEffect(() => {
    if (!quillRef.current) return;
    if (value !== lastValue.current) {
      lastValue.current = value;
      const selection = quillRef.current.getSelection();
      quillRef.current.clipboard.dangerouslyPasteHTML(storageToHtml(value));
      if (selection) { try { quillRef.current.setSelection(selection); } catch(e) {} }
    }
  }, [value]);

  const insertEquation = (latex) => {
    const quill = quillRef.current;
    if (!quill) return;
    window._activeQuill = quill;
    const range = quill.getSelection(true);
    const index = range ? range.index : quill.getLength();
    quill.insertEmbed(index, 'math-chip', { latex }, 'user');
    quill.setSelection(index + 1, 0);
    setShowPopup(false);
    toast.success('✅ Equation inserted!');
    // Sync value
    const stored = htmlToStorage(quill.root.innerHTML);
    lastValue.current = stored;
    onChangeRef.current(stored);
  };

  return (
    <div style={{ position: 'relative' }}>
      {showMath && (
        <div style={{ marginBottom: 6 }}>
          <button type="button" onClick={() => { window._activeQuill = quillRef.current; setShowPopup(true); }} style={{
            padding: '4px 14px', borderRadius: 6, border: '1px solid var(--primary)',
            background: 'rgba(99,102,241,0.1)', color: 'var(--primary)', cursor: 'pointer',
            fontSize: 12, fontWeight: 600,
          }}>∑ Insert Equation</button>
        </div>
      )}
      <div ref={containerRef} style={{ minHeight }} />
      {showPopup && <MathEquationPopup onInsert={insertEquation} onClose={() => setShowPopup(false)} />}
    </div>
  );
}

const emptyQ = { question_text: '', question_type: 'single', options: ['', '', '', ''], correct_answers: [], marks: 1, image: null };

// The backend always hands back start_time/end_time as absolute UTC ISO strings.
// The <input type="datetime-local"> needs a bare "YYYY-MM-DDTHH:mm" wall-clock
// string, and this audience's wall clock is IST (UTC+5:30) — convert back so the
// teacher sees the same local time they originally entered.
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
function utcIsoToIstLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const ist = new Date(d.getTime() + IST_OFFSET_MS);
  const pad = n => String(n).padStart(2, '0');
  return `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}T${pad(ist.getUTCHours())}:${pad(ist.getUTCMinutes())}`;
}

export default function ExamBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, subscription } = useAuth();
  const isScience = user?.role === 'science_teacher';
  const isArts = user?.role === 'arts_teacher';
  const showRichEditor = isScience || isArts;
  const aiCreditsLeft = (subscription?.ai_credit_limit || 0) - (subscription?.ai_credits_used || 0);
  const hasAiCredits = aiCreditsLeft > 0;

  const [exam, setExam] = useState({ title: '', subject: '', instructions: '', duration_minutes: 30, start_time: '', end_time: '', mode: 'tuition', randomize_questions: true, randomize_options: true, allow_result_view: true, allow_pdf_download: false, allow_answer_view: false, allow_solution_view: false, negative_marking: false, negative_marks: 0.25, attempt_limit: 1, require_otp: false });
  const [shareExam, setShareExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [examId, setExamId] = useState(id || null);
  const [activeTab, setActiveTab] = useState('settings');
  const [editingQ, setEditingQ] = useState(null);
  const [qForm, setQForm] = useState(emptyQ);
  const [activeField, setActiveField] = useState(null);
  const [matchingRows, setMatchingRows] = useState([{ col1: '', col2: '' }]);
  const [activeSymbolSection, setActiveSymbolSection] = useState('algebraic');

  // AI Generate from PDF
  const [aiPdf, setAiPdf] = useState(null);
  const [aiMode, setAiMode] = useState('mixed');
  const [aiCount, setAiCount] = useState(10);
  const [aiDifficulty, setAiDifficulty] = useState('medium');
  const [aiMarks, setAiMarks] = useState(1);
  const [aiLanguage, setAiLanguage] = useState('english');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiDrafts, setAiDrafts] = useState([]);
  const [aiFlaggedCount, setAiFlaggedCount] = useState(0);
  const [aiCreditsRemaining, setAiCreditsRemaining] = useState(null);

  useEffect(() => {
    if (id) {
      API.get(`/exams/${id}`).then(r => {
        const { questions: qs, ...rest } = r.data;
        setExam({ ...rest, start_time: utcIsoToIstLocal(rest.start_time), end_time: utcIsoToIstLocal(rest.end_time) });
        setQuestions(qs);
        setExamId(id);
      });
    }
  }, [id]);

  const setE = (k, v) => setExam(f => ({ ...f, [k]: v }));
  const setQ = (k, v) => setQForm(f => ({ ...f, [k]: v }));
  // Quill's text-change listener is bound once at mount (see QuillEditor's [] effect), so any
  // onChange it fires carries a permanently stale `qForm` closure. Deriving the next options
  // array from the updater's own `f` (not the outer `qForm`) keeps every edit index-correct
  // regardless of how stale the calling closure is.
  const setOption = (i, v) => setQForm(f => ({ ...f, options: f.options.map((o, j) => j === i ? v : o) }));

  const saveExamSettings = async (publish = null) => {
    if (exam.start_time && exam.end_time && exam.end_time <= exam.start_time) {
      return toast.error('End time must be after start time');
    }
    setSaving(true);
    try {
      const publishValue = publish !== null ? publish : (exam.is_published || false);
      const payload = { ...exam, is_published: publishValue, start_time: exam.start_time || null, end_time: exam.end_time || null };
      let linkToken = exam.link_token;
      if (examId) {
        await API.put(`/exams/${examId}`, payload);
        setExam(f => ({ ...f, is_published: publishValue }));
      } else {
        const r = await API.post('/exams', payload);
        linkToken = r.data.link_token;
        setExamId(r.data.exam_id);
        navigate(`/teacher/exams/${r.data.exam_id}/edit`, { replace: true });
      }
      if (publish === true) {
        toast.success('🚀 Exam published!');
        setShareExam({ title: exam.title, link_token: linkToken });
      }
      else if (publish === false) toast.success('💾 Saved as draft.');
      else toast.success('✅ Exam settings saved!');
      setActiveTab('questions');
    } catch (err) { toast.error(err.response?.data?.error || 'Error saving'); }
    finally { setSaving(false); }
  };

  const insertSymbolIntoActive = (sym) => {
    // Try Quill first
    const quill = window._activeQuill;
    if (quill) {
      const range = quill.getSelection(true);
      const index = range ? range.index : quill.getLength();
      quill.insertEmbed(index, 'math-chip', { latex: sym }, 'user');
      quill.setSelection(index + 1, 0);
      const stored = htmlToStorage(quill.root.innerHTML);
      // Figure out which field this quill belongs to and update
      if (activeField === 'qtext') setQ('question_text', stored);
      else if (activeField && activeField.startsWith('opt_')) {
        const i = parseInt(activeField.split('_')[1]);
        setOption(i, stored);
      }
      return;
    }
    // Fallback to textarea
    if (!activeField) return toast.error('Click inside a text field first');
    const el = document.getElementById(activeField);
    if (!el) return;
    const start = el.selectionStart, end = el.selectionEnd;
    const val = el.value;
    const newVal = val.slice(0,start) + '$' + sym + '$' + val.slice(end);
    if (activeField === 'qtext') setQ('question_text', newVal);
    else if (activeField.startsWith('opt_')) {
      const i = parseInt(activeField.split('_')[1]);
      setOption(i, newVal);
    }
    setTimeout(() => { el.focus(); el.setSelectionRange(start + sym.length + 2, start + sym.length + 2); }, 10);
  };

  const insertArtsSymbol = (sym) => {
    const quill = window._activeQuill;
    if (quill) {
      const range = quill.getSelection(true);
      const index = range ? range.index : quill.getLength();
      quill.insertEmbed(index, 'math-chip', { latex: sym }, 'user');
      quill.setSelection(index + 1, 0);
      return;
    }
    if (!activeField) return toast.error('Click inside a text field first');
    const el = document.getElementById(activeField);
    if (!el) return;
    const start = el.selectionStart, end = el.selectionEnd;
    const val = el.value;
    const newVal = val.slice(0,start) + '$' + sym + '$' + val.slice(end);
    if (activeField === 'qtext') setQ('question_text', newVal);
    else if (activeField.startsWith('opt_')) {
      const i = parseInt(activeField.split('_')[1]);
      setOption(i, newVal);
    }
  };

  const buildMatchingTable = () => {
    const table = matchingRows.map((r, i) => `${i+1}. ${r.col1} | ${r.col2}`).join('\n');
    setQ('question_text', (qForm.question_text ? qForm.question_text + '\n' : '') + 'Match the following:\n' + table);
  };

  const toggleCorrect = (idx) => {
    const idxStr = String(idx);
    if (qForm.question_type === 'single') { setQ('correct_answers', [idxStr]); }
    else { setQ('correct_answers', qForm.correct_answers.includes(idxStr) ? qForm.correct_answers.filter(x => x !== idxStr) : [...qForm.correct_answers, idxStr]); }
  };

  const saveQuestion = async () => {
    if (!qForm.question_text.trim()) return toast.error('Question text required');
    const filledOptions = qForm.options.filter(o => o.trim());
    if (filledOptions.length < 2) return toast.error('At least 2 options required');
    if (qForm.correct_answers.length === 0) return toast.error('Mark at least one correct answer');
    if (!examId) return toast.error('Save exam settings first');
    const correctTexts = qForm.correct_answers.map(idx => qForm.options[parseInt(idx)]).filter(Boolean);
    if (correctTexts.length === 0) return toast.error('Mark at least one correct answer');
    const fd = new FormData();
    fd.append('question_text', qForm.question_text);
    fd.append('question_type', qForm.question_type);
    fd.append('options', JSON.stringify(filledOptions));
    fd.append('correct_answers', JSON.stringify(correctTexts));
    fd.append('marks', qForm.marks);
    fd.append('order_num', editingQ !== null ? questions[editingQ]?.order_num || 0 : questions.length);
    if (qForm.image) fd.append('image', qForm.image);
    try {
      if (editingQ !== null && questions[editingQ]?.id) {
        await API.put(`/exams/${examId}/questions/${questions[editingQ].id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await API.post(`/exams/${examId}/questions`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('✅ Question saved!');
      const r = await API.get(`/exams/${examId}`);
      setQuestions(r.data.questions);
      setEditingQ(null);
      setQForm(emptyQ);
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const deleteQuestion = async (qid, idx) => {
    if (!window.confirm('Delete this question?')) return;
    await API.delete(`/exams/${examId}/questions/${qid}`);
    setQuestions(q => q.filter((_,i) => i !== idx));
    toast.success('Question deleted');
  };

  const editQuestion = (q, idx) => {
    const opts = [...q.options, ...Array(4).fill('')].slice(0,4);
    const correctIndices = q.correct_answers.map(ans => opts.indexOf(ans)).filter(i => i !== -1).map(String);
    setQForm({ question_text: q.question_text, question_type: q.question_type, options: opts, correct_answers: correctIndices, marks: q.marks, image: null });
    setEditingQ(idx);
  };

  // ── AI Generate from PDF ──
  const generateFromPdf = async () => {
    if (!examId) return toast.error('Save exam settings first');
    if (!aiPdf) return toast.error('Select a PDF file first');
    setAiLoading(true);
    try {
      const fd = new FormData();
      fd.append('pdf', aiPdf);
      fd.append('mode', aiMode);
      fd.append('count', aiCount);
      fd.append('difficulty', aiDifficulty);
      fd.append('marks_per_question', aiMarks);
      fd.append('language', aiLanguage);
      const r = await API.post(`/exams/${examId}/generate-questions`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAiDrafts(r.data.drafts || []);
      setAiFlaggedCount(r.data.flaggedCount || 0);
      setAiCreditsRemaining(r.data.creditsRemaining);
      if (!r.data.drafts || r.data.drafts.length === 0) toast('No verified questions came out of this PDF — try a different mode or PDF.', { icon: '⚠️' });
      else toast.success(`✅ Generated ${r.data.drafts.length} verified question${r.data.drafts.length>1?'s':''}! Review below before saving.`);
    } catch (err) { toast.error(err.response?.data?.error || 'AI generation failed'); }
    finally { setAiLoading(false); }
  };

  const updateDraft = (idx, patch) => setAiDrafts(ds => ds.map((d, i) => i === idx ? { ...d, ...patch } : d));

  const updateDraftOption = (idx, optIdx, val) => setAiDrafts(ds => ds.map((d, i) => {
    if (i !== idx) return d;
    const oldVal = d.options[optIdx];
    const options = d.options.map((o, j) => j === optIdx ? val : o);
    // correct_answers stores verbatim copies of the correct option text — keep them in sync on edit
    const correct_answers = d.correct_answers.map(ca => ca === oldVal ? val : ca);
    return { ...d, options, correct_answers };
  }));

  const toggleDraftCorrect = (idx, optIdx) => setAiDrafts(ds => ds.map((d, i) => {
    if (i !== idx) return d;
    const optText = d.options[optIdx];
    const correct_answers = d.question_type === 'single'
      ? [optText]
      : (d.correct_answers.includes(optText) ? d.correct_answers.filter(x => x !== optText) : [...d.correct_answers, optText]);
    return { ...d, correct_answers };
  }));

  const deleteDraft = idx => setAiDrafts(ds => ds.filter((_, i) => i !== idx));

  const saveAiDrafts = async () => {
    if (aiDrafts.length === 0) return toast.error('No drafts to save');
    const invalid = aiDrafts.find(d => !d.question_text.trim() || d.options.filter(o => o.trim()).length < 2 || d.correct_answers.length === 0);
    if (invalid) return toast.error('Every draft needs question text, at least 2 options, and a marked correct answer');
    setAiSaving(true);
    try {
      const payload = aiDrafts.map(d => ({
        question_text: d.question_text,
        question_type: d.question_type,
        options: d.options.filter(o => o.trim()),
        correct_answers: d.correct_answers,
        marks: d.marks,
        explanation: d.explanation || null,
        gen_source: 'ai',
        verification_status: 'verified',
      }));
      const r = await API.post(`/exams/${examId}/questions/bulk`, { questions: payload });
      toast.success(`✅ Saved ${r.data.count} question${r.data.count>1?'s':''} to the exam!`);
      setAiDrafts([]);
      setAiPdf(null);
      const ref = await API.get(`/exams/${examId}`);
      setQuestions(ref.data.questions);
    } catch (err) { toast.error(err.response?.data?.error || 'Error saving questions'); }
    finally { setAiSaving(false); }
  };

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px' }}>
        <div className="page-header flex-between">
          <div><h1>{id ? '✏️ Edit Exam' : '➕ Create Exam'}</h1></div>
          <div style={{ display: 'flex', gap: 8 }}>
            {['settings', 'questions'].map(t => (
              <button key={t} onClick={() => setActiveTab(t)} className={`btn ${activeTab === t ? 'btn-primary' : 'btn-secondary'}`}>
                {t === 'settings' ? '⚙️ Settings' : `❓ Questions (${questions.length})`}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'settings' && (
          <div className="card">
            <h3 style={{ fontWeight: 700, marginBottom: 20 }}>📋 Exam Settings</h3>
            <div className="form-group"><label>Exam Title *</label><input value={exam.title} onChange={e => setE('title', e.target.value)} placeholder="e.g. Chapter 3 Test" /></div>
            <div className="grid-2">
              <div className="form-group"><label>Subject</label><input value={exam.subject} onChange={e => setE('subject', e.target.value)} placeholder="e.g. Physics" /></div>
              <div className="form-group"><label>Duration (minutes) *</label><input type="number" value={exam.duration_minutes} onChange={e => setE('duration_minutes', +e.target.value)} min={5} /></div>
            </div>
            <div className="grid-2">
              <div className="form-group"><label>Start Time (optional)</label><input type="datetime-local" value={exam.start_time} onChange={e => setE('start_time', e.target.value)} /></div>
              <div className="form-group"><label>End Time (optional)</label><input type="datetime-local" value={exam.end_time} onChange={e => setE('end_time', e.target.value)} /></div>
            </div>
            <div className="form-group"><label>Mode</label>
              <select value={exam.mode} onChange={e => setE('mode', e.target.value)}>
                <option value="tuition">Tuition Mode (Roll number optional)</option>
                <option value="school">School Mode (Roll number mandatory)</option>
              </select>
            </div>
            <div className="form-group"><label>Instructions</label><textarea value={exam.instructions} onChange={e => setE('instructions', e.target.value)} rows={3} placeholder="Any instructions for students..." /></div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
              {[
                { key: 'allow_result_view', label: '👁️ Allow students to view result' },
                { key: 'allow_pdf_download', label: '📄 Allow PDF download' },
                { key: 'allow_answer_view', label: '🔑 Show correct answers to students' },
                { key: 'allow_solution_view', label: '💡 Show explanations/solutions to students' },
                { key: 'randomize_questions', label: '🔀 Randomize question order' },
                { key: 'randomize_options', label: '🔀 Randomize option order' },
                { key: 'negative_marking', label: '➖ Enable negative marking' },
                { key: 'require_otp', label: '📲 Require phone verification (OTP) to start' },
              ].map(o => (
                <label key={o.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: 'var(--text)' }}>
                  <input type="checkbox" checked={!!exam[o.key]} onChange={e => setE(o.key, e.target.checked)} style={{ width: 16, height: 16 }} />
                  {o.label}
                </label>
              ))}
            </div>
            {exam.negative_marking && (
              <div className="form-group">
                <label>Negative marks per wrong answer</label>
                <input type="number" value={exam.negative_marks} onChange={e => setE('negative_marks', +e.target.value)} min={0.25} max={4} step={0.25} style={{ maxWidth: 120 }} />
              </div>
            )}
            <div className="grid-2">
              <div className="form-group"><label>Attempt Limit (0 = unlimited)</label><input type="number" value={exam.attempt_limit} onChange={e => setE('attempt_limit', +e.target.value)} min={0} max={10} /></div>
              <div className="form-group"><label>Exam Password (optional)</label><input value={exam.exam_password || ''} onChange={e => setE('exam_password', e.target.value)} placeholder="Leave blank for no password" /></div>
            </div>
            <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
              <button className="btn btn-secondary btn-lg" onClick={() => saveExamSettings(false)} disabled={saving}>{saving ? '⏳ Saving...' : '💾 Save as Draft'}</button>
              <button className="btn btn-primary btn-lg" onClick={() => saveExamSettings(true)} disabled={saving}>{saving ? '⏳ Saving...' : '🚀 Save & Publish'}</button>
              {examId && <span style={{ fontSize:13, color: exam.is_published ? '#34d399' : '#f59e0b', fontWeight:600 }}>{exam.is_published ? '✅ Published' : '📝 Draft'}</span>}
            </div>
          </div>
        )}

        {activeTab === 'questions' && (
          <div>

            {/* AI Generate from PDF */}
            <div className="card" style={{ marginBottom: 16, border: '1px solid rgba(99,102,241,0.35)' }}>
              <h3 style={{ fontWeight: 700, marginBottom: 4 }}>🤖 AI Generate from PDF</h3>
              {!hasAiCredits ? (
                <div style={{ textAlign: 'center', padding: '20px 12px' }}>
                  <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 4, lineHeight: 1.7 }}>
                    Upload a PDF (notes, chapter, past paper) and let AI draft MCQs from it, independently re-solved and verified before you see them.
                  </p>
                  <p style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14, marginTop: 12, marginBottom: 2 }}>
                    AI question generation is a paid feature — upgrade to generate questions from your PDFs
                  </p>
                  <p style={{ color: 'var(--primary-light)', fontWeight: 700, fontSize: 14, marginBottom: 16 }}>
                    AI প্রশ্ন তৈরি একটি পেইড ফিচার — PDF থেকে প্রশ্ন তৈরি করতে আপগ্রেড করুন
                  </p>
                  <Link to="/teacher/subscribe" className="btn btn-primary">🚀 Upgrade Now</Link>
                </div>
              ) : (
                <>
                  <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 16 }}>
                    Upload a PDF (notes, chapter, past paper) and let AI draft MCQs from it. Every question is independently re-solved and verified before it's shown to you — only verified questions cost credits.
                    {aiCreditsRemaining !== null && <strong style={{ color: 'var(--primary-light)' }}> • {aiCreditsRemaining} AI credit{aiCreditsRemaining===1?'':'s'} remaining</strong>}
                  </p>
                  <div className="form-group">
                    <label>PDF File *</label>
                    <input type="file" accept="application/pdf" onChange={e => setAiPdf(e.target.files[0])} style={{ padding: 8 }} disabled={aiLoading} />
                  </div>
                  <div className="grid-2">
                    <div className="form-group"><label>Mode</label>
                      <select value={aiMode} onChange={e => setAiMode(e.target.value)} disabled={aiLoading}>
                        <option value="theory">Theory</option>
                        <option value="numerical">Numerical</option>
                        <option value="mixed">Mixed</option>
                        <option value="match">Match the PDF</option>
                      </select>
                    </div>
                    <div className="form-group"><label>Difficulty</label>
                      <select value={aiDifficulty} onChange={e => setAiDifficulty(e.target.value)} disabled={aiLoading}>
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid-2">
                    <div className="form-group"><label>Number of Questions (max 30)</label>
                      <input type="number" value={aiCount} onChange={e => setAiCount(Math.max(1, Math.min(30, +e.target.value)))} min={1} max={30} disabled={aiLoading} />
                    </div>
                    <div className="form-group"><label>Marks per Question</label>
                      <input type="number" value={aiMarks} onChange={e => setAiMarks(Math.max(1, +e.target.value))} min={1} disabled={aiLoading} />
                    </div>
                  </div>
                  <div className="form-group"><label>Language</label>
                    <select value={aiLanguage} onChange={e => setAiLanguage(e.target.value)} disabled={aiLoading}>
                      <option value="english">English</option>
                      <option value="bengali">Bengali (বাংলা)</option>
                    </select>
                  </div>
                  <button className="btn btn-primary" onClick={generateFromPdf} disabled={aiLoading}>
                    {aiLoading ? '⏳ Generating & verifying... (30-60s)' : '✨ Generate Questions'}
                  </button>
                </>
              )}

              {hasAiCredits && aiDrafts.length > 0 && (
                <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                    <h4 style={{ fontWeight: 700, margin: 0 }}>📝 Review Drafts ({aiDrafts.length})</h4>
                    <span className="badge badge-success">✅ {aiDrafts.length} verified</span>
                    {aiFlaggedCount > 0 && <span className="badge badge-warning">⚠️ {aiFlaggedCount} flagged &amp; discarded (didn't pass verification)</span>}
                  </div>
                  {aiDrafts.map((d, i) => (
                    <div key={i} style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                        <span className={`badge ${d.question_type==='multiple'?'badge-warning':'badge-info'}`}>{d.question_type}</span>
                        <button onClick={() => deleteDraft(i)} className="btn btn-danger btn-sm">🗑️ Discard</button>
                      </div>
                      <div className="form-group">
                        <label style={{ fontSize: 12 }}>Question</label>
                        <textarea value={d.question_text} onChange={e => updateDraft(i, { question_text: e.target.value })} rows={2} />
                        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>Preview: <MathPreview text={d.question_text} /></div>
                      </div>
                      <div className="form-group">
                        <label style={{ fontSize: 12 }}>Options (click ✅ to mark correct)</label>
                        {d.options.map((opt, oi) => (
                          <div key={oi} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                            <span style={{ fontWeight: 700, color: 'var(--text2)', width: 20 }}>{String.fromCharCode(65+oi)}.</span>
                            <input value={opt} onChange={e => updateDraftOption(i, oi, e.target.value)} style={{ flex: 1 }} />
                            <button onClick={() => toggleDraftCorrect(i, oi)} style={{
                              padding: '5px 10px', borderRadius: 6, border: '1px solid', cursor: 'pointer', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap',
                              background: d.correct_answers.includes(opt) ? 'var(--success)' : 'var(--card)',
                              color: d.correct_answers.includes(opt) ? 'white' : 'var(--text2)',
                              borderColor: d.correct_answers.includes(opt) ? 'var(--success)' : 'var(--border)',
                            }}>{d.correct_answers.includes(opt) ? '✅' : 'Mark'}</button>
                          </div>
                        ))}
                      </div>
                      <div className="grid-2">
                        <div className="form-group"><label style={{ fontSize: 12 }}>Marks</label>
                          <input type="number" value={d.marks} onChange={e => updateDraft(i, { marks: +e.target.value })} min={1} />
                        </div>
                      </div>
                      <div className="form-group">
                        <label style={{ fontSize: 12 }}>Explanation / Worked Solution</label>
                        <textarea value={d.explanation || ''} onChange={e => updateDraft(i, { explanation: e.target.value })} rows={2} />
                      </div>
                    </div>
                  ))}
                  <button className="btn btn-primary btn-full" onClick={saveAiDrafts} disabled={aiSaving}>
                    {aiSaving ? '⏳ Saving...' : `💾 Save ${aiDrafts.length} Question${aiDrafts.length>1?'s':''} to Exam`}
                  </button>
                </div>
              )}
            </div>

            {/* Arts symbols */}
            {isArts && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text2)', marginBottom: 10 }}>🎨 Greek & Special Symbols</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {ARTS_SYMBOLS.map(s => (
                    <button key={s.label} onClick={() => insertArtsSymbol(s.insert)} style={{
                      padding: '5px 11px', borderRadius: 6, border: '1px solid var(--border)',
                      background: 'var(--card2)', color: 'var(--text)', cursor: 'pointer', fontSize: 13,
                    }}>{s.label}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Matching table */}
            {showRichEditor && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text2)', marginBottom: 10 }}>📊 Matching Table Builder</div>
                {matchingRows.map((row, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input value={row.col1} onChange={e => setMatchingRows(r => r.map((x,j) => j===i ? {...x, col1: e.target.value} : x))} placeholder={`Column A ${i+1}`} style={{ flex: 1 }} />
                    <input value={row.col2} onChange={e => setMatchingRows(r => r.map((x,j) => j===i ? {...x, col2: e.target.value} : x))} placeholder={`Column B ${i+1}`} style={{ flex: 1 }} />
                    {matchingRows.length > 1 && <button onClick={() => setMatchingRows(r => r.filter((_,j) => j!==i))} className="btn btn-danger btn-sm">✕</button>}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setMatchingRows(r => [...r, { col1: '', col2: '' }])}>➕ Row</button>
                  <button className="btn btn-primary btn-sm" onClick={buildMatchingTable}>📋 Insert into Question</button>
                </div>
              </div>
            )}

            {/* Question form */}
            <div className="card" style={{ marginBottom: 20 }}>
              <h3 style={{ fontWeight: 700, marginBottom: 16 }}>{editingQ !== null ? '✏️ Edit Question' : '➕ Add Question'}</h3>

              <div className="form-group">
                <label>Question Text * {showRichEditor && <span style={{ color: 'var(--text3)', fontWeight: 400, fontSize: 12 }}>(type freely — click ∑ to insert equation)</span>}</label>
                {showRichEditor ? (
                  <QuillEditor
                    id="qtext"
                    value={qForm.question_text}
                    onChange={v => { setQ('question_text', v); setActiveField('qtext'); }}
                    placeholder="Type your question here..."
                    minHeight={80}
                    showMath={true}
                  />
                ) : (
                  <textarea id="qtext" value={qForm.question_text} onChange={e => setQ('question_text', e.target.value)} onFocus={() => setActiveField('qtext')} rows={3} placeholder="Type your question here..." />
                )}
              </div>

              <div className="form-group">
                <label>Question Image (optional)</label>
                <input type="file" accept="image/*" onChange={e => setQ('image', e.target.files[0])} style={{ padding: 8 }} />
              </div>
              <div className="grid-2">
                <div className="form-group"><label>Question Type</label>
                  <select value={qForm.question_type} onChange={e => { setQ('question_type', e.target.value); setQ('correct_answers', []); }}>
                    <option value="single">Single correct answer</option>
                    <option value="multiple">Multiple correct answers</option>
                  </select>
                </div>
                <div className="form-group"><label>Marks</label>
                  <input type="number" value={qForm.marks} onChange={e => setQ('marks', +e.target.value)} min={1} />
                </div>
              </div>

              <div className="form-group">
                <label>Options * (mark correct answer{qForm.question_type==='multiple'?' (multiple)':''} by clicking ✅)</label>
                {qForm.options.map((opt, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-start' }}>
                    <span style={{ fontWeight: 700, color: 'var(--text2)', width: 24, paddingTop: 8, flexShrink: 0 }}>{String.fromCharCode(65+i)}.</span>
                    <div style={{ flex: 1 }}>
                      {showRichEditor ? (
                        <QuillEditor
                          id={`opt_${i}`}
                          value={opt}
                          onChange={v => { setOption(i, v); setActiveField(`opt_${i}`); }}
                          placeholder={`Option ${String.fromCharCode(65+i)}`}
                          minHeight={44}
                          showMath={true}
                        />
                      ) : (
                        <input id={`opt_${i}`} value={opt}
                          onChange={e => setOption(i, e.target.value)}
                          onFocus={() => setActiveField(`opt_${i}`)}
                          placeholder={`Option ${String.fromCharCode(65+i)}`} style={{ width: '100%' }} />
                      )}
                    </div>
                    <button onClick={() => toggleCorrect(i)} style={{
                      padding: '6px 10px', borderRadius: 6, border: '1px solid', cursor: 'pointer', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0, marginTop: 4,
                      background: qForm.correct_answers.includes(String(i)) ? 'var(--success)' : 'var(--card2)',
                      color: qForm.correct_answers.includes(String(i)) ? 'white' : 'var(--text2)',
                      borderColor: qForm.correct_answers.includes(String(i)) ? 'var(--success)' : 'var(--border)',
                    }}>{qForm.correct_answers.includes(String(i)) ? '✅ Correct' : 'Mark'}</button>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" onClick={saveQuestion}>💾 Save Question</button>
                {editingQ !== null && <button className="btn btn-secondary" onClick={() => { setEditingQ(null); setQForm(emptyQ); }}>Cancel</button>}
              </div>
            </div>

            {/* Questions list */}
            {questions.length > 0 && (
              <div className="card">
                <h3 style={{ fontWeight: 700, marginBottom: 16 }}>📋 Questions ({questions.length})</h3>
                {questions.map((q, i) => (
                  <div key={q.id} style={{ padding: '14px 0', borderBottom: i < questions.length-1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ fontWeight: 700, color: 'var(--primary-light)', minWidth: 24 }}>Q{i+1}.</span>
                          <span style={{ fontSize: 14 }}><MathPreview text={q.question_text} /></span>
                          <span className={`badge ${q.question_type === 'multiple' ? 'badge-warning' : 'badge-info'}`}>{q.question_type}</span>
                          <span className="badge badge-success">{q.marks}m</span>
                        </div>
                        {q.question_image && <img src={q.question_image} alt="" style={{ maxHeight: 60, borderRadius: 6, marginBottom: 6 }} />}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {q.options.map((opt, j) => (
                            <span key={j} style={{
                              padding: '3px 10px', borderRadius: 6, fontSize: 12,
                              background: q.correct_answers.includes(opt) ? 'rgba(16,185,129,0.2)' : 'var(--card2)',
                              color: q.correct_answers.includes(opt) ? '#34d399' : 'var(--text2)',
                              border: `1px solid ${q.correct_answers.includes(opt) ? 'rgba(16,185,129,0.4)' : 'var(--border)'}`,
                              fontWeight: q.correct_answers.includes(opt) ? 700 : 400
                            }}>{String.fromCharCode(65+j)}. <MathPreview text={opt} /></span>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => editQuestion(q, i)} className="btn btn-secondary btn-sm">✏️</button>
                        <button onClick={() => deleteQuestion(q.id, i)} className="btn btn-danger btn-sm">🗑️</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {shareExam && <ShareExamModal exam={shareExam} onClose={() => setShareExam(null)} />}
    </div>
  );
}
